import { type FC, useState } from "react";
import { IconButton, Stack, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import moment, { type Moment } from "moment";
import { useSnackbar } from "notistack";
import { ConfirmDialog } from "@/admin/shared/ui";
import { useUpdateProductPurchaseMutation } from "@/admin/entities/productPurchases";

interface EditPurchaseButtonProps {
  id: string;
  startAt: string;
  expiredAt: string;
}

/**
 * Правка сроков покупки: два пикера в диалоге подтверждения с задержкой,
 * как в легаси EditButton.
 */
export const EditPurchaseButton: FC<EditPurchaseButtonProps> = ({
  id,
  startAt,
  expiredAt,
}) => {
  const [open, setOpen] = useState(false);
  const [startAtValue, setStartAtValue] = useState<Moment | null>(
    moment(startAt)
  );
  const [expiredAtValue, setExpiredAtValue] = useState<Moment | null>(
    moment(expiredAt)
  );
  const { enqueueSnackbar } = useSnackbar();

  const [updateProductPurchase, { loading, error, reset }] =
    useUpdateProductPurchaseMutation({
      refetchQueries: ["ProductPurchasesList"],
      onCompleted: () => {
        enqueueSnackbar("Покупка обновлена", { variant: "success" });
        setOpen(false);
      },
      onError: () => {
        // ошибка уже отображается через ErrorMessage внутри ConfirmDialog
      },
    });

  // Пикер отдаёт невалидный moment на недобранную дату, такой ISO был бы
  // null: до проверки сабмит уходил бы пустым и рапортовал успех.
  const datesValid = Boolean(
    startAtValue?.isValid() && expiredAtValue?.isValid()
  );

  const handleOpen = () => {
    setStartAtValue(moment(startAt));
    setExpiredAtValue(moment(expiredAt));
    // Ошибку предыдущей попытки в заново открытом диалоге показывать незачем.
    reset();
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
    reset();
  };

  const handleConfirm = () => {
    if (!startAtValue || !expiredAtValue || !datesValid) return;

    void updateProductPurchase({
      variables: {
        id,
        input: {
          startAt: startAtValue.toISOString(),
          expiredAt: expiredAtValue.toISOString(),
        },
      },
    });
  };

  return (
    <>
      <Tooltip title="Изменить сроки покупки">
        <span>
          <IconButton
            size="small"
            disabled={loading}
            onClick={(event) => {
              event.stopPropagation();
              handleOpen();
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <ConfirmDialog
        open={open}
        loading={loading}
        error={error}
        delay={5}
        maxWidth="sm"
        disabled={!datesValid}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        confirmTitle="Изменить сроки покупки"
        confirmText="Доступ зрителя к продукту будет ограничен новыми датами."
      >
        <Stack spacing={2}>
          <DateTimePicker
            label="Начало доступа"
            value={startAtValue}
            ampm={false}
            format="DD MMMM YYYY HH:mm"
            onChange={(value) => setStartAtValue(value)}
            slotProps={{ textField: { fullWidth: true } }}
          />
          <DateTimePicker
            label="Окончание доступа"
            value={expiredAtValue}
            ampm={false}
            format="DD MMMM YYYY HH:mm"
            onChange={(value) => setExpiredAtValue(value)}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Stack>
      </ConfirmDialog>
    </>
  );
};
